// Template-specific mapping functions for template 691f80ce0664e043e4a1986d

export function mapV1FieldsToV2Data(fields: any) {
  const additional = fields?.additional_fields || {};
  const v2: any = {
    title: fields?.title || '',
    subtitle: additional?.subtitle || '',
    published_at: fields?.published_at || fields?.wertentwicklung_seit_auflage_end_date || additional?.dateBadge || '',
    
    // Master Data (Stammdaten)
    masterData: additional?.masterData || fields?.stammdaten || {
      fondskategorie: additional?.fondskategorie || '',
      auflagedatum: additional?.auflagedatum || fields?.auflagedatum || '',
      wkn: additional?.wkn || fields?.wkn || '',
      isin: additional?.isin || fields?.isin || '',
      erstausgabepreis: additional?.erstausgabepreis || '',
      fondsvolumen: additional?.fondsvolumen || '',
      kursNav: additional?.kursNav || fields?.nav || '',
      fondswaehrung: additional?.fondswaehrung || fields?.fondswaehrung || '',
      fondsdomizil: additional?.fondsdomizil || fields?.fondsdomizil || '',
      geschaeftsjahresende: additional?.geschaeftsjahresende || '',
      ertragsverwendung: additional?.ertragsverwendung || fields?.ertragsverwendung || '',
      rechtsform: additional?.rechtsform || '',
      risikoErtragsprofil: additional?.risikoErtragsprofil || '',
      sparplanfaehig: additional?.sparplanfaehig || '',
      mindestzeichnungbetrag: additional?.mindestzeichnungbetrag || '',
    },
    
    // Fees (Gebühren)
    fees: additional?.fees || {
      ausgabeaufschlag: additional?.ausgabeaufschlag || fields?.konditionen?.issueSurcharge || '',
      laufendeKostenTer: additional?.laufendeKostenTer || fields?.konditionen?.ongoingCosts || '',
      davonVerwaltungsgebuehr: additional?.davonVerwaltungsgebuehr || fields?.konditionen?.managementFee || '',
    },
    
    // Fund Manager
    fundManager: additional?.fundManager || {
      name: additional?.fundManagerName || '',
      company: additional?.fundManagerCompany || '',
      research: additional?.fundManagerResearch || '',
      company2: additional?.fundManagerCompany2 || '',
      phone: additional?.fundManagerPhone || '',
      hedgingRatio: additional?.hedgingRatio || '',
      photoUrl: additional?.fundManagerPhotoUrl || '',
    },
    
    // Monthly Commentary
    monthlyCommentary: additional?.monthlyCommentary || '',
    // Legal notice shown instead of chart and performance values (fund younger than 12 months). Empty = off.
    performanceNotice: typeof additional?.performanceNotice === 'string' ? additional.performanceNotice : '',
    // Words of the monthly commentary shown on page 1 (default 175); the rest continues on page 2.
    commentaryFirstPartWords: additional?.commentaryFirstPartWords ?? undefined,
    
    // Explanatory Text
    explanatoryText: additional?.explanatoryText || '',
    
    // Chart Image URL
    chartImageUrl: additional?.chartImageUrl || '',
    
    // Awards Image URL
    awardsImageUrl: additional?.awardsImageUrl || '',
    
    // Rolling Performance
    rollingPerformance: additional?.rollingPerformance || {
      periods: [],
    },
    
    // Cumulative Performance
    cumulativePerformance: additional?.cumulativePerformance || {
      oneMonth: '',
      sixMonths: '',
      oneYear: '',
      threeYears: '',
      fiveYears: '',
      currentYear: '',
      sinceInception: '',
    },
    
    // Investment Strategy
    investmentStrategy: additional?.investmentStrategy || '',
    
    // Target Group & Risk
    targetGroup: additional?.targetGroup || '',
    investmentHorizon: additional?.investmentHorizon || '',
    riskIndicator: additional?.riskIndicator || 0,
    
    // Opportunities and Risks
    opportunities: additional?.opportunities || [],
    risks: additional?.risks || [],
    
    // Sales Prospectus
    salesProspectus: additional?.salesProspectus || '',
    
    // Legal Notes
    legalNotes: additional?.legalNotes || {
      verwahrstelle: '',
      verwaltungsgesellschaft: '',
      text: '',
    },
    
    // Top 10 Stocks
    top10Stocks: additional?.top10Stocks || [],
    
    // Allocations
    sectorAllocation: additional?.sectorAllocation || [],
    currencyAllocation: additional?.currencyAllocation || [],
    assetAllocation: additional?.assetAllocation || [],
    
    // Section Headers
    sectionHeaders: additional?.sectionHeaders || {},
    
    // Chart overrides (optional)
    historyIsin: additional?.historyIsin || fields?.isin || '',
    historyUntil: additional?.historyUntil || fields?.wertentwicklung_seit_auflage_end_date,
    historyLegendLabel: additional?.historyLegendLabel || fields?.title || '',
    historyCurrencySymbol: additional?.historyCurrencySymbol || '€',
  };
  return v2;
}

function deepEqual(a: any, b: any) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

export function buildCustomizePayload(v2Data: any, original: any) {
  const payload: Array<{ key: string; value: any }> = [];
  const safeOriginal = original || {};
  const origAdditional = safeOriginal.additional_fields || {};

  // Title
  if ((safeOriginal.title || '') !== (v2Data?.title || '')) {
    payload.push({ key: 'title', value: v2Data?.title || '' });
  }

  // Published at
  const nextPublished = v2Data?.published_at;
  if (nextPublished && safeOriginal.published_at !== nextPublished) {
    payload.push({ key: 'published_at', value: nextPublished });
  }

  // Master Data - check if any fields changed
  if (v2Data?.masterData) {
    const origMasterData = safeOriginal.stammdaten || {};
    const newMasterData = v2Data.masterData;
    
    // Check individual fields that might be in core fields
    if (newMasterData.wkn && !deepEqual(origMasterData.wkn, newMasterData.wkn)) {
      if (!payload.find(p => p.key === 'wkn')) {
        payload.push({ key: 'wkn', value: newMasterData.wkn });
      }
    }
    if (newMasterData.isin && !deepEqual(origMasterData.isin, newMasterData.isin)) {
      if (!payload.find(p => p.key === 'isin')) {
        payload.push({ key: 'isin', value: newMasterData.isin });
      }
    }
  }

  // Build updated additional_fields
  const updatedAdditional: any = {};
  
  if (!deepEqual(origAdditional.subtitle, v2Data?.subtitle)) {
    updatedAdditional.subtitle = v2Data?.subtitle;
  }
  
  if (!deepEqual(origAdditional.dateBadge, v2Data?.published_at)) {
    updatedAdditional.dateBadge = v2Data?.published_at;
  }
  
  // Master Data
  if (v2Data?.masterData && !deepEqual(origAdditional.masterData, v2Data.masterData)) {
    updatedAdditional.masterData = v2Data.masterData;
  }
  
  // Fees
  if (v2Data?.fees && !deepEqual(origAdditional.fees, v2Data.fees)) {
    updatedAdditional.fees = v2Data.fees;
  }
  
  // Fund Manager
  if (v2Data?.fundManager && !deepEqual(origAdditional.fundManager, v2Data.fundManager)) {
    updatedAdditional.fundManager = v2Data.fundManager;
  }
  
  // Monthly Commentary
  if (!deepEqual(origAdditional.monthlyCommentary, v2Data?.monthlyCommentary)) {
    updatedAdditional.monthlyCommentary = v2Data?.monthlyCommentary;
  }

  // Performance notice
  if (!deepEqual(origAdditional.performanceNotice || '', v2Data?.performanceNotice || '')) {
    updatedAdditional.performanceNotice = v2Data?.performanceNotice || '';
  }

  // Commentary split
  if (!deepEqual(origAdditional.commentaryFirstPartWords, v2Data?.commentaryFirstPartWords)) {
    updatedAdditional.commentaryFirstPartWords = v2Data?.commentaryFirstPartWords;
  }
  
  // Explanatory Text
  if (!deepEqual(origAdditional.explanatoryText, v2Data?.explanatoryText)) {
    updatedAdditional.explanatoryText = v2Data?.explanatoryText;
  }
  
  // Chart Image URL
  if (!deepEqual(origAdditional.chartImageUrl, v2Data?.chartImageUrl)) {
    updatedAdditional.chartImageUrl = v2Data?.chartImageUrl;
  }
  
  // Awards Image URL
  if (!deepEqual(origAdditional.awardsImageUrl, v2Data?.awardsImageUrl)) {
    updatedAdditional.awardsImageUrl = v2Data?.awardsImageUrl;
  }
  
  // Rolling Performance
  if (v2Data?.rollingPerformance && !deepEqual(origAdditional.rollingPerformance, v2Data.rollingPerformance)) {
    updatedAdditional.rollingPerformance = v2Data.rollingPerformance;
  }
  
  // Cumulative Performance
  if (v2Data?.cumulativePerformance && !deepEqual(origAdditional.cumulativePerformance, v2Data.cumulativePerformance)) {
    updatedAdditional.cumulativePerformance = v2Data.cumulativePerformance;
  }
  
  // Investment Strategy
  if (!deepEqual(origAdditional.investmentStrategy, v2Data?.investmentStrategy)) {
    updatedAdditional.investmentStrategy = v2Data?.investmentStrategy;
  }
  
  // Target Group & Risk
  if (!deepEqual(origAdditional.targetGroup, v2Data?.targetGroup)) {
    updatedAdditional.targetGroup = v2Data?.targetGroup;
  }
  if (!deepEqual(origAdditional.investmentHorizon, v2Data?.investmentHorizon)) {
    updatedAdditional.investmentHorizon = v2Data?.investmentHorizon;
  }
  if (!deepEqual(origAdditional.riskIndicator, v2Data?.riskIndicator)) {
    updatedAdditional.riskIndicator = v2Data?.riskIndicator;
  }
  
  // Opportunities and Risks
  if (v2Data?.opportunities && !deepEqual(origAdditional.opportunities, v2Data.opportunities)) {
    updatedAdditional.opportunities = v2Data.opportunities;
  }
  if (v2Data?.risks && !deepEqual(origAdditional.risks, v2Data.risks)) {
    updatedAdditional.risks = v2Data.risks;
  }
  
  // Sales Prospectus
  if (!deepEqual(origAdditional.salesProspectus, v2Data?.salesProspectus)) {
    updatedAdditional.salesProspectus = v2Data?.salesProspectus;
  }
  
  // Legal Notes
  if (v2Data?.legalNotes && !deepEqual(origAdditional.legalNotes, v2Data.legalNotes)) {
    updatedAdditional.legalNotes = v2Data.legalNotes;
  }
  
  // Top 10 Stocks
  if (v2Data?.top10Stocks && !deepEqual(origAdditional.top10Stocks, v2Data.top10Stocks)) {
    updatedAdditional.top10Stocks = v2Data.top10Stocks;
  }
  
  // Allocations
  if (v2Data?.sectorAllocation && !deepEqual(origAdditional.sectorAllocation, v2Data.sectorAllocation)) {
    updatedAdditional.sectorAllocation = v2Data.sectorAllocation;
  }
  if (v2Data?.currencyAllocation && !deepEqual(origAdditional.currencyAllocation, v2Data.currencyAllocation)) {
    updatedAdditional.currencyAllocation = v2Data.currencyAllocation;
  }
  if (v2Data?.assetAllocation && !deepEqual(origAdditional.assetAllocation, v2Data.assetAllocation)) {
    updatedAdditional.assetAllocation = v2Data.assetAllocation;
  }
  
  // Section Headers
  if (v2Data?.sectionHeaders && !deepEqual(origAdditional.sectionHeaders, v2Data.sectionHeaders)) {
    updatedAdditional.sectionHeaders = v2Data.sectionHeaders;
  }
  
  // Chart overrides
  if (!deepEqual(origAdditional.historyIsin, v2Data?.historyIsin)) {
    updatedAdditional.historyIsin = v2Data?.historyIsin;
  }
  if (!deepEqual(origAdditional.historyUntil, v2Data?.historyUntil)) {
    updatedAdditional.historyUntil = v2Data?.historyUntil;
  }
  if (!deepEqual(origAdditional.historyLegendLabel, v2Data?.historyLegendLabel)) {
    updatedAdditional.historyLegendLabel = v2Data?.historyLegendLabel;
  }
  if (!deepEqual(origAdditional.historyCurrencySymbol, v2Data?.historyCurrencySymbol)) {
    updatedAdditional.historyCurrencySymbol = v2Data?.historyCurrencySymbol;
  }

  // Update core V1 end-date field if changed
  if (v2Data?.historyUntil && !deepEqual(safeOriginal.wertentwicklung_seit_auflage_end_date, v2Data?.historyUntil)) {
    payload.push({ key: 'wertentwicklung_seit_auflage_end_date', value: v2Data?.historyUntil });
  }

  // Add additional_fields if there are any updates
  if (Object.keys(updatedAdditional).length) {
    const merged = { ...origAdditional, ...updatedAdditional };
    payload.push({ key: 'additional_fields', value: merged });
  }

  return payload;
}
